// Gemeinsame Bausteine. Bewusst klein gehalten — kein Design-System, nur die
// Wiederholungen, die sonst überall auseinanderlaufen.
import { useEffect, useRef } from 'react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';

export function Karte({ children, klasse = '' }: { children: ReactNode; klasse?: string }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 ${klasse}`}>
      {children}
    </div>
  );
}

export function KartenKopf({ titel, hinweis, aktion }: { titel: string; hinweis?: ReactNode; aktion?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
      <div>
        <h2 className="font-semibold text-slate-900 dark:text-slate-100">{titel}</h2>
        {hinweis && <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{hinweis}</p>}
      </div>
      {aktion}
    </div>
  );
}

type KnopfProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variante?: 'primaer' | 'sekundaer' | 'gefahr' | 'still';
  laedt?: boolean;
};

export function Knopf({ variante = 'sekundaer', laedt, children, className = '', disabled, ...rest }: KnopfProps) {
  const basis = 'inline-flex items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50';
  const stile = {
    primaer: 'bg-marke-600 text-white hover:bg-marke-700',
    sekundaer: 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700',
    gefahr: 'bg-red-600 text-white hover:bg-red-700',
    still: 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
  }[variante];

  return (
    <button className={`${basis} ${stile} ${className}`} disabled={disabled || laedt} {...rest}>
      {laedt && <Spinner klein />}
      {children}
    </button>
  );
}

export function Feld({ label, fehler, hinweis, className = '', ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: string; fehler?: string; hinweis?: string }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">{label}</span>
      <input
        // Eine übergebene className wird ANGEHÄNGT, nicht eingesetzt: läge sie
        // in {...rest}, würde sie Rahmen, Höhe und Farben ersetzen und das Feld
        // damit unsichtbar machen.
        className={`w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400
          dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500
          ${fehler ? 'border-red-500 dark:border-red-500' : 'border-slate-300 dark:border-slate-700'} ${className}`}
        aria-invalid={!!fehler}
        {...rest}
      />
      {fehler && <span className="mt-1 block text-sm text-red-600 dark:text-red-400">{fehler}</span>}
      {hinweis && !fehler && <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">{hinweis}</span>}
    </label>
  );
}

export function Auswahl({ label, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { label: string }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">{label}</span>
      <select
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
        {...rest}
      >
        {children}
      </select>
    </label>
  );
}

export function Spinner({ klein }: { klein?: boolean }) {
  return (
    <span
      role="status"
      aria-label="Lädt"
      className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${klein ? 'h-3.5 w-3.5' : 'h-5 w-5'}`}
    />
  );
}

export function Hinweis({ art = 'info', titel, children }: { art?: 'info' | 'warnung' | 'fehler' | 'erfolg'; titel?: string; children: ReactNode }) {
  const stile = {
    info:    'border-marke-200 bg-marke-50 text-marke-900 dark:border-marke-900 dark:bg-marke-950/40 dark:text-marke-100',
    warnung: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100',
    fehler:  'border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100',
    erfolg:  'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100',
  }[art];

  return (
    <div className={`rounded-lg border px-4 py-3 text-sm ${stile}`} role={art === 'fehler' ? 'alert' : undefined}>
      {titel && <p className="mb-0.5 font-semibold">{titel}</p>}
      {children}
    </div>
  );
}

export function Abzeichen({ art = 'neutral', children }: { art?: 'neutral' | 'gruen' | 'rot' | 'gelb' | 'blau'; children: ReactNode }) {
  const stile = {
    neutral: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    gruen:   'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
    rot:     'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
    gelb:    'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300',
    blau:    'bg-marke-100 text-marke-800 dark:bg-marke-950 dark:text-marke-300',
  }[art];
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${stile}`}>{children}</span>;
}

export function LeerZustand({ titel, text, aktion }: { titel: string; text?: string; aktion?: ReactNode }) {
  return (
    <div className="px-6 py-12 text-center">
      <p className="font-medium text-slate-900 dark:text-slate-100">{titel}</p>
      {text && <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500 dark:text-slate-400">{text}</p>}
      {aktion && <div className="mt-4">{aktion}</div>}
    </div>
  );
}

/** Hinweis, wenn eine Funktion nicht im gebuchten Tarif enthalten ist. */
export function TarifSperre({ was, upgradeAuf }: { was: string; upgradeAuf?: string }) {
  return (
    <Karte klasse="p-6">
      <Hinweis art="warnung" titel={`${was} ist in Ihrem Tarif nicht enthalten`}>
        {upgradeAuf
          ? <>Verfügbar ab dem Tarif <strong>{upgradeAuf}</strong>. Sie können den Tarif unter „Tarif &amp; Abrechnung“ wechseln.</>
          : <>Bitte wenden Sie sich an den Support, um diese Funktion freizuschalten.</>}
      </Hinweis>
    </Karte>
  );
}

/**
 * Rückt einen Bereich ins Blickfeld, sobald er erscheint.
 *
 * Gebraucht überall dort, wo eine Antwort oben auf der Seite auftaucht, der
 * auslösende Knopf aber weiter unten in einer langen Liste sitzt. Ohne das
 * öffnet sich die Karte außerhalb des Bildschirms: für den Benutzer passiert
 * scheinbar nichts, und bei einer einmaligen Anzeige — etwa einem zurück-
 * gesetzten Passwort — ist die Information danach unwiederbringlich weg.
 *
 * `ausloeser` wird gebraucht, wenn der Bereich stehen bleibt und sich nur sein
 * Inhalt ändert: dann rückt er bei jedem neuen Wert erneut ins Bild.
 */
export function Blickfang({ children, ausloeser, klasse = '' }: {
  children: ReactNode;
  ausloeser?: unknown;
  klasse?: string;
}) {
  const kasten = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Wer ruhige Oberflächen eingestellt hat, bekommt den Sprung ohne Animation.
    const ruhig = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    kasten.current?.scrollIntoView({ behavior: ruhig ? 'auto' : 'smooth', block: 'center' });
  }, [ausloeser]);

  return <div ref={kasten} className={klasse}>{children}</div>;
}
